import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Megaphone, ChevronRight, ArrowLeft, Send, Loader2, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { normalizePhone } from '@/utils/phoneUtils';

interface Template {
  id: string;
  name: string;
  content: string;
  channel: string | null;
  category: string | null;
  description: string | null;
  metadata: Record<string, any> | null;
  variables: any;
  is_active: boolean;
}

interface ChatDisparoTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: string;
  instanceName?: string | null;
  orgId: string;
  contactPhone: string;
  contactName?: string | null;
  integrationType: string;
}

const WEBHOOK_URLS: Record<string, string> = {
  'whatsapp-zenvia': 'https://webhook.fluity.space/webhook/fluity-sender/zenvia-dispatch',
  'whatsapp-meta':   'https://webhook.fluity.space/webhook/fluity-sender/meta-dispatch',
};

function renderTemplate(content: string, vars: Record<string, string>): string {
  return content.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}

function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
}

export function ChatDisparoTemplateDialog({
  open,
  onOpenChange,
  instanceId,
  instanceName,
  orgId,
  contactPhone,
  contactName,
  integrationType,
}: ChatDisparoTemplateDialogProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<'list' | 'fill' | 'success'>('list');

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setVariables({});
      setStep('list');
      setLoadError(null);
      return;
    }
    loadTemplates();
  }, [open, orgId]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('api_disparos_messages')
        .select('id, name, content, channel, category, description, metadata, variables, is_active')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setTemplates((data || []) as Template[]);
    } catch (err: any) {
      const msg = err?.message || 'Erro desconhecido ao carregar templates';
      console.error('[ChatDisparoTemplateDialog] Erro ao buscar templates:', err);
      setLoadError(msg);
      toast.error('Erro ao carregar templates de disparo');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectTemplate = (tpl: Template) => {
    setSelectedTemplate(tpl);
    const vars = extractVariables(tpl.content || '');
    const initial: Record<string, string> = {};
    if (contactName) {
      if (vars.includes('nome')) initial['nome'] = contactName;
      if (vars.includes('name')) initial['name'] = contactName;
    }
    setVariables(initial);
    setStep('fill');
  };

  const templateVars = useMemo(
    () => (selectedTemplate ? extractVariables(selectedTemplate.content || '') : []),
    [selectedTemplate],
  );

  const renderedPreview = useMemo(
    () => (selectedTemplate ? renderTemplate(selectedTemplate.content || '', variables) : ''),
    [selectedTemplate, variables],
  );

  const allVarsFilled = templateVars.every(v => variables[v]?.trim());

  const handleSend = async () => {
    if (!selectedTemplate || !allVarsFilled) return;

    const webhookUrl = WEBHOOK_URLS[integrationType.toLowerCase()];
    if (!webhookUrl) {
      toast.error(`Disparo via ${integrationType} ainda não suportado nesta interface.`);
      return;
    }

    setSending(true);
    try {
      const phone = normalizePhone(contactPhone);
      const renderedMsg = renderTemplate(selectedTemplate.content || '', variables);

      const { data: campaign, error: campaignError } = await supabase
        .from('sender_campaigns')
        .insert({
          name: `Chat - ${selectedTemplate.name} - ${new Date().toISOString()}`,
          org_id: orgId,
          instance_id: instanceId,
          channel: 'whatsapp',
          template_id: selectedTemplate.id,
          template_content_snapshot: selectedTemplate.content || '',
          template_name_snapshot: selectedTemplate.name,
          template_variables_snapshot: templateVars,
          status: 'running',
          source_type: 'contacts',
          total_valid: 1,
          total_recipients: 1,
        })
        .select('id')
        .single();

      if (campaignError) throw campaignError;

      const { error: recipientError } = await supabase
        .from('campaign_recipients')
        .insert({
          campaign_id: campaign.id,
          org_id: orgId,
          phone,
          normalized_phone: phone,
          name: contactName || null,
          variables,
          rendered_message: renderedMsg,
          status: 'pending',
        });

      if (recipientError) throw recipientError;

      const resp = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: campaign.id,
          org_id: orgId,
          instance_id: instanceId,
          instance_name: instanceName || '',
          channel: 'whatsapp',
          template_id: selectedTemplate.id,
          template_content: selectedTemplate.content || '',
          template_variables: templateVars,
          total_valid: 1,
        }),
      });

      if (!resp.ok) {
        let errTitle = 'Erro ao enviar template';
        let errDescription: string | undefined;
        try {
          const body = await resp.json();
          const apiError = body?.error;
          if (apiError) {
            errTitle = apiError.error_user_title || apiError.message || errTitle;
            errDescription = apiError.error_user_msg;
          } else {
            errTitle = body?.message || body?.detail || errTitle;
          }
        } catch {
          errDescription = await resp.text().catch(() => resp.statusText);
        }
        toast.error(errTitle, errDescription ? { description: errDescription } : undefined);
        return;
      }

      setStep('success');
      setTimeout(() => onOpenChange(false), 2000);
    } catch (err: any) {
      console.error('[ChatDisparoTemplateDialog] Erro ao disparar template:', err);
      toast.error('Erro ao enviar template: ' + (err?.message || 'Erro desconhecido'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-violet-600" />
            {step === 'list'
              ? 'Selecionar Template'
              : step === 'fill'
              ? 'Preencher Variáveis'
              : 'Template Enviado'}
          </DialogTitle>
        </DialogHeader>

        {step === 'list' && (
          <div className="space-y-3">
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-muted-foreground text-center max-w-[260px]">
                  Não foi possível carregar os templates.<br />
                  <span className="text-xs text-destructive/80">{loadError}</span>
                </p>
                <Button variant="outline" size="sm" onClick={loadTemplates} className="gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Tentar novamente
                </Button>
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                Nenhum template ativo encontrado para esta organização.
              </p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-3">
                  {templates.map(tpl => (
                    <button
                      key={tpl.id}
                      onClick={() => handleSelectTemplate(tpl)}
                      className="w-full text-left p-3 rounded-xl border border-border hover:border-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/20 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{tpl.name}</p>
                          {tpl.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{tpl.description}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
                            {tpl.content}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {tpl.category && (
                            <Badge variant="outline" className="text-[10px]">{tpl.category}</Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        {step === 'fill' && selectedTemplate && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('list')}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3 w-3" />
              Voltar para templates
            </button>

            <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50 rounded-xl p-3">
              <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 mb-1.5">
                {selectedTemplate.name}
              </p>
              <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">{renderedPreview}</p>
            </div>

            {templateVars.length > 0 && (
              <div className="space-y-3">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Variáveis do template
                </p>
                {templateVars.map(varName => (
                  <div key={varName}>
                    <Label className="text-xs text-muted-foreground capitalize">{varName}</Label>
                    <Input
                      value={variables[varName] || ''}
                      onChange={e =>
                        setVariables(prev => ({ ...prev, [varName]: e.target.value }))
                      }
                      placeholder={`Valor para {{${varName}}}`}
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={handleSend}
                disabled={sending || !allVarsFilled}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Template
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'success' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="text-sm font-medium text-foreground">Template enviado com sucesso!</p>
            <p className="text-xs text-muted-foreground text-center">
              O disparo foi iniciado e aparecerá no chat em breve.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
